import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, TextField, Tooltip, Typography } from "@material-ui/core";
import { EditOutlined as EditIcon } from "@material-ui/icons";
import type { ReactNode } from "react";

/**
 * A value that reads as text until you click it, then becomes an input, then
 * goes back to reading as text when you leave it.
 *
 * Used for the listing details a realtor rarely needs to touch (price, rooms,
 * sqm, and the hand-written redacted address). Rendering those as permanent
 * form inputs made the card look like a data-entry chore and buried the fields
 * that ARE missing among five that aren't. As read-only text they stay
 * glanceable, and the edit affordance is one click away when it's wanted.
 */

export type InlineEditableValueProps = {
  /** Caption above / beside the value. Also the input's label while editing. */
  label: string;
  /** Raw value being edited (what the input holds). */
  value: string;
  /** Rendered when not editing. Falls back to an em-dash when empty. */
  displayValue?: string;
  onChange: (next: string) => void;
  icon?: ReactNode;
  numeric?: boolean;
  /** Full-width block (address) vs. an inline chip in a row (figures). */
  variant?: "block" | "inline";
};

export default function InlineEditableValue({
  label,
  value,
  displayValue,
  onChange,
  icon,
  numeric = false,
  variant = "inline"
}: InlineEditableValueProps): JSX.Element {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Autofocus on entering edit mode. `autoFocus` alone is unreliable here —
  // the input mounts inside a Collapse, and MUI's transition can steal focus
  // back mid-animation.
  useEffect(() => {
    if (!editing) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [editing]);

  const shown = (displayValue ?? value).trim();
  const isEmpty = !shown;

  if (editing) {
    return (
      // Fills its slot exactly — the PARENT owns the geometry. Sizing itself
      // (a flex-grow basis) made the field expand on click and shove its
      // siblings around, which is what the read/edit swap must never do.
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <TextField
          size="small"
          fullWidth
          inputRef={inputRef}
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            // Enter commits (the value is already live — this just exits edit
            // mode); Escape does the same rather than reverting, because every
            // keystroke has already been applied and un-applying them would
            // need a snapshot the caller doesn't keep.
            if (e.key === "Enter" || e.key === "Escape") {
              e.preventDefault();
              setEditing(false);
            }
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={numeric ? { inputMode: "decimal", dir: "ltr" } : undefined}
        />
      </Box>
    );
  }

  return (
    // Inline values show only an icon and a number, so the tooltip carries the
    // label — it's the only place "95" is identified as square meters.
    <Tooltip
      title={
        variant === "inline" ? label : (t("signatures.newDoc.listingDetails.editAction") as string)
      }
    >
      {/* The whole thing is the control — one button, not a container holding a
          button. A nested IconButton here would be a second focus stop that
          does nothing on its own, so the pencil is a plain decorative icon.
          The label stays INSIDE the clickable area so clicking it starts an
          edit, even though it sits on its own line in the block variant. */}
      <Stack
        justifyContent="center"
        spacing={0.25}
        role="button"
        tabIndex={0}
        aria-label={`${t("signatures.newDoc.listingDetails.editAction")} — ${label}`}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
        sx={{
          cursor: "pointer",
          borderRadius: 1,
          px: 0.75,
          py: variant === "block" ? 0.75 : 0,
          // Matches the height of the small TextField that replaces it, so the
          // card doesn't jump vertically when a value enters or leaves edit mode.
          minHeight: 40,
          width: "100%",
          minWidth: 0,
          transition: "background-color 150ms ease",
          "&:hover, &:focus-visible": { bgcolor: "action.hover" },
          // The pencil is a hint, not a control — it only firms up on hover so
          // a row of three figures doesn't read as a row of three buttons.
          "&:hover .inline-edit-pencil, &:focus-visible .inline-edit-pencil": { opacity: 1 }
        }}
      >
        {/* Block variant: caption on its own line above the value — these
            labels are full sentences and don't fit beside a long address. */}
        {variant === "block" && (
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        )}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ width: "100%", minWidth: 0 }}
        >
          {icon}
          <Typography
            variant="body2"
            noWrap={variant === "inline"}
            sx={{
              minWidth: 0,
              fontWeight: variant === "block" ? 600 : 500,
              color: isEmpty ? "text.disabled" : "text.primary"
            }}
          >
            {isEmpty ? (t("signatures.newDoc.listingDetails.valueEmpty") as string) : shown}
          </Typography>
          <EditIcon
            className="inline-edit-pencil"
            aria-hidden
            sx={{
              fontSize: 15,
              flexShrink: 0,
              color: "text.secondary",
              opacity: 0.35,
              transition: "opacity 150ms ease"
            }}
          />
        </Stack>
      </Stack>
    </Tooltip>
  );
}
